import MuxPlayer, { type MuxPlayerProps } from "@mux/mux-player-react";
import { clsx } from "clsx";
import { useId, useRef, useState } from "react";

import {
	buildMuxPlayerConfig,
	type MuxPlayerMetadataInput,
} from "~/lib/video/buildMuxPlayerConfig";
import { shouldRetryMuxPlayback } from "~/lib/video/muxPlaybackRecovery";
import { videoFullscreenHostClassName } from "~/lib/video/videoFullscreenShell";

interface Props
	extends Omit<MuxPlayerProps, "src" | "playbackId" | "metadata"> {
	playbackId: string;
	thumbnailUrl: string;
	blurDataURL?: string;
	duration?: number;
	metadata?: MuxPlayerMetadataInput | null;
	fullscreenElement?: string;
}

const VideoPlayer = ({
	thumbnailUrl,
	playbackId,
	blurDataURL: _blurDataURL,
	duration,
	metadata,
	fullscreenElement,
	className,
	style,
	onError,
	onLoadedData,
	...props
}: Props) => {
	const reactId = useId();
	const hostId = fullscreenElement ?? `mux-fs-${reactId.replace(/:/g, "")}`;
	const retryAttemptRef = useRef(0);
	const [reloadToken, setReloadToken] = useState(0);

	const config = buildMuxPlayerConfig({
		playbackId,
		thumbnailUrl,
		metadata,
		fullscreenElementId: hostId,
		defaultDuration: duration,
	});

	return (
		<div id={hostId} className={videoFullscreenHostClassName()}>
			<MuxPlayer
				key={`${playbackId}-${reloadToken}`}
				playsInline
				defaultHiddenCaptions={true}
				autoPlay={false}
				accentColor="#0ea5e9"
				{...props}
				{...config}
				className={clsx("h-full w-full", className)}
				style={style}
				onLoadedData={(event) => {
					retryAttemptRef.current = 0;
					onLoadedData?.(event);
				}}
				onError={(event) => {
					if (shouldRetryMuxPlayback(retryAttemptRef.current)) {
						retryAttemptRef.current += 1;
						setReloadToken((token) => token + 1);
					}
					onError?.(event);
				}}
			/>
		</div>
	);
};

export default VideoPlayer;
